"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { useErrorToast } from "@/hooks/useErrorToast";
import { useOTPCooldown } from "@/hooks/useOTPCooldown";
import { parsePhoneNumber } from "@/lib/util/string/phone";
import { Button } from "@/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/ui/form";
import { Input } from "@/ui/input";
import { createClientComponentClient } from "@supabase/auth-helpers-nextjs";
import { useRouter } from "next/navigation";

const FormSchema = z.object({
  phone: z.string().optional(),
  email: z.string().optional(),
});

export function SignInFormOtp({ type }: { type: "phone" | "email" }) {
  const { showErrorToast } = useErrorToast();
  const router = useRouter();
  const supabase = createClientComponentClient();
  const { withRateLimit } = useOTPCooldown(type);
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      phone: "",
      email: "",
    },
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (type === "phone") {
      const phone = parsePhoneNumber(data.phone || "");
      if (!phone) {
        showErrorToast({ error: new Error("Invalid Phone Number Format") });
        form.setFocus("phone");
        form.reset();
        return;
      }

      try {
        await withRateLimit(async () => {
          const { error } = await supabase.auth.signInWithOtp({
            phone: phone,
            options: {
              shouldCreateUser: false,
            },
          });

          if (error) {
            showErrorToast({ error });
            form.setFocus("phone");
            form.reset();
            return;
          }

          router.push(`/auth/verify?phone=${encodeURIComponent(phone)}`);
          router.refresh();
        });
      } catch (error) {
        showErrorToast({ error: error as Error });
      }
    } else {
      // Type == Email
      const email = data.email;
      if (!email) {
        showErrorToast({ error: new Error("Invalid Email") });
        form.setFocus("email");
        form.reset();
        return;
      }

      try {
        await withRateLimit(async () => {
          const { error } = await supabase.auth.signInWithOtp({
            email: email,
            options: {
              shouldCreateUser: false,
            },
          });

          if (error) {
            showErrorToast({ error });
            form.setFocus("email");
            form.reset();
          } else {
            router.refresh();
            router.push(
              `/v2/auth/verify/email?email=${encodeURIComponent(email)}`
            );
          }
        });
      } catch (error) {
        showErrorToast({ error: error as Error });
      }
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, (err) => console.log(err))}
        className="space-y-6"
      >
        {type === "phone" ? (
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number*</FormLabel>
                <FormControl>
                  <Input placeholder="Phone Number" type="tel" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : (
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email*</FormLabel>
                <FormControl>
                  <Input placeholder="Email" type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <Button className="w-full" type="submit">
          Submit
        </Button>
      </form>
    </Form>
  );
}
